/**
 * Controls (docs/spec/04 §7): rebind the seven actions (two keys each), touch controls, reset, and
 * the gamepad's fixed buttons for reference.
 */

import type { Action } from '../../platform/options';
import type { Screen } from '../../render/screen';
import { UiScene } from '../kit/base';
import { drawRule } from '../kit/chrome';
import { notOurs } from '../kit/input';
import type { Rect } from '../kit/menuModel';
import { inRect, wrapIndex } from '../kit/menuModel';
import type { RowColumns, RowValue } from '../kit/optionRow';
import { drawOptionRow, rowPress } from '../kit/optionRow';
import { clip } from '../kit/text';
import { ACCENT, BAD, blink, CX, FAINT, INK, LEFT, MUTED, RIGHT, SELECTED } from '../kit/theme';
import type { KeyInput, MenuAction, PointerInput } from '../scene';
import {
  ACTION_LABELS, ACTION_ORDER, ACTION_WORDS, bindKey, canBind, clearKey, holderOf, resetKeys, TOUCH_VALUES, unbound,
} from './controlsModel';

const HEAD_Y = 30;
const ROW0 = 44;
const ROW_H = 14;
const LABEL_X = 16;
const SLOT_X = [68, 144] as const;
const SLOT_W = 72;
/** Rows 0-6 are the actions; then TOUCH and RESET. */
const TOUCH_ROW = ACTION_ORDER.length;
const RESET_ROW = TOUCH_ROW + 1;
const ROWS = RESET_ROW + 1;
const COLS: RowColumns = { label: LABEL_X, right: RIGHT - 8 };
const PAD_Y = 192;
const NOTE_Y = 240;
const PAD_LINES = ['GAMEPAD BUTTONS ARE FIXED:', 'A = FIRE   B OR X = NOVA', 'START = PAUSE'];

function rowY(row: number): number {
  return ROW0 + row * ROW_H + (row >= TOUCH_ROW ? 10 : 0);
}

function slotRect(row: number, slot: number): Rect {
  return { x: SLOT_X[slot] ?? SLOT_X[0], y: rowY(row) - 3, w: SLOT_W, h: 13 };
}

export class ControlsScene extends UiScene {
  protected readonly heading = 'CONTROLS';
  protected override readonly track = null;
  private row = 0;
  private slot: 0 | 1 = 0;
  private waiting = false;
  private note = '';
  private noteBad = false;
  private noteAt = -9999;

  private get act(): Action | undefined {
    return ACTION_ORDER[this.row];
  }

  private name(code: string): string {
    return code ? this.services.input.keyName(code) : '---';
  }

  private touchValue(): RowValue {
    return { kind: 'choice', text: this.services.options.get().touch.toUpperCase() };
  }

  protected override describe(): string {
    return `Controls. ${this.rowText()}`;
  }

  private rowText(): string {
    const a = this.act;
    const o = this.services.options.get();
    if (!a) return this.row === TOUCH_ROW ? `Touch controls: ${o.touch}` : 'Reset to defaults';
    const code = o.keys[a][this.slot];
    return `${ACTION_WORDS[a]}, key ${this.slot + 1}: ${code ? this.name(code) : 'none'}`;
  }

  protected override hint(): string {
    if (this.waiting) return 'ESC CANCELS   DEL CLEARS';
    if (this.act) return `${this.words().ok} REBIND   DEL CLEAR`;
    return this.row === TOUCH_ROW ? '◄► CHANGE' : `${this.words().ok} RESET`;
  }

  protected override touchHint(): string {
    return this.waiting ? 'TAP TO CANCEL' : 'KEYS NEED A KEYBOARD';
  }

  protected paint(screen: Screen): void {
    const keys = this.services.options.get().keys;
    screen.text('ACTION', LABEL_X, HEAD_Y, MUTED);
    SLOT_X.forEach((x, s) => screen.text(`KEY ${s + 1}`, x + SLOT_W / 2, HEAD_Y, MUTED, 'center'));
    ACTION_ORDER.forEach((a, row) => {
      const y = rowY(row);
      const on = row === this.row;
      if (on) screen.text('►', LEFT, y, SELECTED);
      screen.text(ACTION_LABELS[a], LABEL_X, y, on ? SELECTED : INK);
      keys[a].forEach((code, slot) => {
        const r = slotRect(row, slot);
        const sel = on && slot === this.slot;
        const wait = sel && this.waiting;
        screen.box(r.x, r.y, r.w, r.h, sel ? SELECTED : FAINT);
        const label = wait ? (blink(this.ms, 700) ? '?' : '') : clip(this.name(code), 8);
        screen.text(label, r.x + r.w / 2, r.y + 3, wait ? SELECTED : code ? ACCENT : FAINT, 'center');
      });
    });
    drawOptionRow(screen, rowY(TOUCH_ROW), 'TOUCH CONTROLS', this.touchValue(), this.row === TOUCH_ROW, this.ms, COLS);
    const reset = this.row === RESET_ROW;
    if (reset) screen.text('►', LEFT, rowY(RESET_ROW), SELECTED);
    screen.text('RESET TO DEFAULTS', LABEL_X, rowY(RESET_ROW), reset ? SELECTED : INK);
    drawRule(screen, PAD_Y - 8, this.ms, FAINT);
    const pad = this.services.input.gamepad();
    PAD_LINES.forEach((line, i) => screen.text(line, CX, PAD_Y + i * 12, pad ? INK : MUTED, 'center'));
    this.paintNote(screen, unbound(keys)[0]);
  }

  /** The line at the foot: what to do now, what just happened, or what is wrong. */
  private paintNote(screen: Screen, missing: Action | undefined): void {
    const a = this.act;
    if (this.waiting && a) screen.text(`PRESS A KEY FOR ${ACTION_LABELS[a]}`, CX, NOTE_Y, SELECTED, 'center');
    else if (this.ms - this.noteAt < 2200) screen.text(this.note, CX, NOTE_Y, this.noteBad ? BAD : ACCENT, 'center');
    else if (missing) screen.text(`NO KEY FOR ${ACTION_LABELS[missing]}!`, CX, NOTE_Y, BAD, 'center');
  }

  private tell(text: string, bad = false): void {
    this.note = clip(text, 26);
    this.noteBad = bad;
    this.noteAt = this.ms;
    this.sound(bad ? 'error' : 'select');
    this.say(text);
  }

  private move(row: number, slot: 0 | 1 = this.slot): void {
    const next = wrapIndex(row, ROWS);
    if (next === this.row && slot === this.slot) return;
    this.row = next;
    this.slot = slot;
    this.sound('move');
    this.say(this.rowText());
  }

  private activate(): void {
    const a = this.act;
    if (a) {
      this.waiting = true;
      this.sound('select');
      this.say(`Press a key for ${ACTION_WORDS[a]}. Escape cancels.`);
    } else if (this.row === RESET_ROW) {
      this.services.options.update({ keys: resetKeys() });
      this.tell('KEYS RESET TO DEFAULTS');
    } else this.cycleTouch(1);
  }

  private cycleTouch(delta: number): void {
    const i = TOUCH_VALUES.indexOf(this.services.options.get().touch);
    this.services.options.update({ touch: TOUCH_VALUES[wrapIndex(i + delta, TOUCH_VALUES.length)] ?? 'auto' });
    this.sound('move');
    this.say(this.rowText());
  }

  private clear(a: Action): void {
    this.services.options.update({ keys: clearKey(this.services.options.get().keys, a, this.slot) });
    this.waiting = false;
    this.sound('back');
    this.say(`${ACTION_WORDS[a]}, key ${this.slot + 1} cleared`);
  }

  private bind(a: Action, code: string): void {
    if (!canBind(a, code)) return this.sound('error');
    const before = this.services.options.get().keys;
    const from = holderOf(before, code);
    const next = bindKey(before, a, this.slot, code);
    this.waiting = false;
    const key = clip(this.name(code), 8);
    if (!next) return this.tell(`${key}: ${from ? ACTION_LABELS[from] : ''}'S ONLY KEY`, true);
    this.services.options.update({ keys: next });
    const moved = from !== null && from !== a;
    this.tell(moved ? `${key}: ${ACTION_LABELS[from]} TO ${ACTION_LABELS[a]}` : `${ACTION_LABELS[a]} = ${key}`);
  }

  keydown(k: KeyInput): boolean {
    const a = this.act;
    if (!a) return false;
    if (!this.waiting) {
      // Backspace alone is the menus' "back"; Delete (or Shift+Backspace) empties the slot.
      const wipe = k.code === 'Delete' || (k.code === 'Backspace' && k.shift);
      if (wipe) this.clear(a);
      return wipe;
    }
    // Reload and the like stay the browser's, even now.
    if (notOurs(k)) return false;
    // Escape is Pause's own key: there it binds, everywhere else it cancels.
    if (k.code === 'Escape' && a !== 'pause') this.cancel();
    else if (k.code === 'Delete' || k.code === 'Backspace') this.clear(a);
    else this.bind(a, k.code);
    return true;
  }

  private cancel(): void {
    this.waiting = false;
    this.sound('back');
    this.say('Cancelled');
  }

  protected override back(): void {
    if (this.waiting) this.cancel();
    else super.back();
  }

  protected onAction(a: MenuAction): void {
    if (this.waiting) return;
    if (a === 'up') this.move(this.row - 1);
    else if (a === 'down') this.move(this.row + 1);
    else if (a === 'left' || a === 'right') {
      if (this.act) this.move(this.row, a === 'left' ? 0 : 1);
      else if (this.row === TOUCH_ROW) this.cycleTouch(a === 'left' ? -1 : 1);
    } else if (a === 'confirm') this.activate();
  }

  protected override onPointer(p: PointerInput): void {
    if (p.kind === 'down' && p.button !== 0) return;
    // With no keyboard to hand, a tap anywhere gives up on the key.
    if (this.waiting) return p.kind === 'down' ? this.cancel() : undefined;
    if (p.kind !== 'move' && p.kind !== 'down') return;
    for (let row = 0; row < ROWS; row++) {
      const y = rowY(row);
      if (p.y < y - 3 || p.y >= y - 3 + ROW_H) continue;
      const slot: 0 | 1 = row < TOUCH_ROW && p.x >= SLOT_X[1] - 2 ? 1 : 0;
      this.move(row, row < TOUCH_ROW ? slot : this.slot);
      if (p.kind !== 'down') return;
      if (row === TOUCH_ROW) {
        const press = rowPress(this.touchValue(), COLS, p.x);
        this.cycleTouch('step' in press ? press.step : 1);
      } else if (row === RESET_ROW || inRect(slotRect(row, slot), p.x, p.y)) this.activate();
      return;
    }
  }
}
